import React from 'react'
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import AgTechHeaderDetail from '@agtech/web/components/Headers/AgTechHeaderDetail';
import AgTechBadgedTabPage from '@agtech/web/components/Pages/Tabbed/Flavors/AgTechBadgeTabPage';
import { AgTechTabPageProps } from '@agtech/web/components/Pages/Tabbed/AgTechTabPage';
import { IndexLength } from '@agtech/core/utilities/AgTechUtilities';
import { AgTechContact } from '@agtech/data/contacts/ContactData';
import { SaleContactDetails } from '@sales/data/operation/contacts/SaleContactData'
import { getSaleContactCreditsTabPageProps } from './SaleContactCreditsTabPage';
import ContactNotesCardCollection from '@agtech/web/integrations/contacts/components/cards/ContactNotesCardCollection';
import ContactNoteDialog from '@agtech/web/integrations/contacts/dialogs/notes/ContactNoteDialog';

export const getSaleContactDetailTabPageProps = (props: { contactDetails: SaleContactDetails }): AgTechTabPageProps => {
    const contactDetails = props.contactDetails;
    const contactNotes = contactDetails.notes ?? {};
    const contactCredits = contactDetails.credits ?? {};

    return {
        tabs: [
            { id: 'contacts-general-tab', text: 'General', content: () => <SaleContactGeneralInformationFields contact={contactDetails.contact} /> },
            { id: 'contacts-notes-tab', text: 'Notes', displayText: `Notes (${IndexLength(contactNotes)})`, content: () => <SaleContactNotesTab contactDetails={contactDetails} /> },
            { id: 'contacts-credits-tab', text: 'Credits', displayText: `Credits (${IndexLength(contactCredits)})`, content: () => <SaleContactCreditsTab contactDetails={contactDetails} /> }
        ]
    }
}

const SaleContactGeneralInformationFields = (props: { contact: AgTechContact }) => {
    return (
        <div className='row-based stretched container'>
            <AgTechFormRow>
                <AgTechFormTextControl
                    entityType={props.contact}
                    caption='First Name'
                    value={cnt => cnt.firstName}
                    onControlValueChanged={async (e, val) => ({ ...e, firstName: val })}
                />
                <AgTechFormTextControl
                    entityType={props.contact}
                    caption='Last Name'
                    value={cnt => cnt.lastName}
                    onControlValueChanged={async (e, val) => ({ ...e, lastName: val })}
                />
                <AgTechFormTextControl
                    entityType={props.contact}
                    caption='Ranch Name'
                    value={cnt => cnt.ranchName}
                    onControlValueChanged={async (e, val) => ({ ...e, ranchName: val })}
                />
            </AgTechFormRow>
            <AgTechFormSection header='Contact Information'>
                <AgTechFormRow>
                    <AgTechFormTextControl
                        entityType={props.contact}
                        caption='Email Address'
                        value={cnt => cnt.emailAddress}
                        onControlValueChanged={async (e, val) => ({ ...e, emailAddress: val })}
                        classes='w-[240px]'
                    />
                    <AgTechFormTextControl
                        entityType={props.contact}
                        caption='Phone Number'
                        value={cnt => cnt.phoneNumber}
                        onControlValueChanged={async (e, val) => ({ ...e, phoneNumber: val })}
                    />
                </AgTechFormRow>
            </AgTechFormSection>
            <AgTechFormSection header='Address Information'>
                <AgTechFormRow>
                    <AgTechFormTextControl
                        entityType={props.contact}
                        caption='Address'
                        value={cnt => cnt.address}
                        onControlValueChanged={async (e, val) => ({ ...e, address: val })}
                        width={100}
                    />
                </AgTechFormRow>
                <AgTechFormRow>
                    <AgTechFormTextControl
                        entityType={props.contact}
                        caption='City'
                        value={cnt => cnt.city}
                        onControlValueChanged={async (e, val) => ({ ...e, city: val })}
                    />
                    <AgTechFormTextControl
                        entityType={props.contact}
                        caption='State'
                        value={cnt => cnt.stateAbbreviation}
                        onControlValueChanged={async (e, val) => ({ ...e, stateAbbreviation: val })}
                    />
                    <AgTechFormTextControl
                        entityType={props.contact}
                        caption='Zip'
                        value={cnt => cnt.zip}
                        onControlValueChanged={async (e, val) => ({ ...e, zip: val })}
                    />
                </AgTechFormRow>
            </AgTechFormSection>
        </div>
    )
}

const SaleContactNotesTab = (props: { contactDetails: SaleContactDetails }) => {
    return (
        <div className='stretched flex'>
            <ContactNotesCardCollection
                contactDetails={props.contactDetails}
                customize={collectionProps => ({
                    ...collectionProps,
                    settings: {
                        ...collectionProps.settings,
                        header: () => (
                            <AgTechHeaderDetail
                                header={`Notes (${IndexLength(props.contactDetails.notes)})`}
                                settings={{
                                    header: {
                                        classes: '!text-plus'
                                    },
                                    content: {
                                        body: () => (
                                            <ContactNoteDialog.Button
                                                contactId={props.contactDetails.contact.id}
                                                classes='ml-auto button-highlight'
                                            />
                                        )
                                    }
                                }}
                            />
                        )
                    }
                })}
            />
        </div>
    )
}

const SaleContactCreditsTab = (props: { contactDetails: SaleContactDetails }) => {
    return (
        <AgTechBadgedTabPage
            {...getSaleContactCreditsTabPageProps({ contactDetails: props.contactDetails })}
        />
    )
}
